/**
 * Ritual Service
 * Handles all ritual-related API calls
 */

import { apiClient } from "@/lib/api-client"
import { getAssetUrl } from "@/utils/assets"
import { getRitualTypeFormat, type RitualTypeEnum, type RitualVisibilityEnum } from "../types"
import type {
  RitualDetailData,
  RitualFormat,
  RitualMembershipDetails,
  RitualMembersResponse,
  RitualSavedUsersResponse,
} from "../types/detail.types"

// Types - matching backend enums exactly
export type RitualType = RitualTypeEnum
export type RitualVisibility = RitualVisibilityEnum
export type ParticipationFee = "Free" | "Paid" | "Donation-based"
export type AccessType = "PUBLIC" | "PRIVATE" // Backend enum, can be passed through
export type RepeatOption = "none" | "daily" | "weekly" | "monthly" | "yearly" | "custom"

export interface RitualOrganizer {
  type: "user" | "community"
  id: string
  inviteNote?: string
}

export interface RitualDuplicateOrganizer {
  type: "user" | "community"
  id: string
  name: string | null
  username: string | null
  profilePictureUrl: string | null
  inviteNote?: string
  isCreator?: boolean
}

export interface PrivacySettings {
  whoCanSeeYourRitual: string[]
  whoCanComment: string[]
  whoCanEditRitual: string[]
  whoCanJoin: string[]
  whoCanInvite?: string[]
  whoCanSeeParticipantList?: string[]
}

export interface RitualOrganizerUser {
  id: string
  username: string | null
  name: string | null
  profilePictureUrl: string | null
  coverImage: string | null
  isFollowing: boolean
  followerCount: number
}

export interface RitualOrganizerCommunity {
  id: string
  name: string | null
  handle: string | null
  coverImage: string | null
  isMember: boolean
  totalMemberCount: number
}

export interface RitualOrganizerItem {
  id: string
  organiserType: "user" | "community"
  organiserId: string
  status: "PENDING" | "Accepted" | "DECLINED"
  inviteNote: string | null
  invitedAt: string | null
  joinedAt: string | null
  user?: RitualOrganizerUser | null
  community?: RitualOrganizerCommunity | null
}

export interface RitualOrganizersResponse {
  items: RitualOrganizerItem[]
  pagination: {
    page: number
    limit: number
    totalItems: number
    totalPages: number
    hasNextPage: boolean
    hasPrevPage: boolean
  }
}

export interface VideoInput {
  video_url: string
  thumbnail_url?: string
  title?: string
}

/**
 * Media input for unified media payload.
 * Each entry corresponds 1:1 with entries in the files[] array.
 */
export interface MediaInput {
  type: "IMAGE" | "VIDEO" | "AUDIO"
  isCover?: boolean
  title?: string
  posterIndex?: number | null
  videoThumbnailIndex?: number | null
  sortOrder?: number
  /**
   * For existing media items, the uploadId to identify which media to update.
   * Used when updating thumbnails for existing videos/audios.
   */
  uploadId?: string
}

export interface VideoChanges {
  add?: VideoInput[]
  edit?: Array<{
    id: string
    video_url: string
    thumbnail_url?: string
    title?: string
    sort_order?: number
  }>
  delete?: string[]
}

export interface CreateRitualRequest {
  title: string
  type: RitualType
  date: string // ISO date string
  repeat: RepeatOption
  repeatAt?: string // For custom repeat value (e.g., "7")
  repeatAtType?: "DAYS" | "HOURS" | "DATE" // For custom repeat type
  duration: number // in minutes
  streetAddress?: string
  city?: string
  state?: string
  lat?: number
  long?: number
  country?: string
  participationFee: ParticipationFee
  courseVideoAccess?: "ALL" | "SEQUENTIAL"
  price?: number
  feeDetails?: string
  description?: string
  whatToBring?: string
  experienceDetails?: string
  intention?: string
  intentionIds?: string[]
  steps?: string
  meetingLink?: string
  webOrSocialMediaLink?: string
  accessType: RitualVisibility // Matches backend field name
  communityId?: string
  tags?: string[]
  organizers?: RitualOrganizer[]
  privacySettings: PrivacySettings
  minParticipantRequirement?: boolean
  minParticipants?: number
  registrationDeadline?: string // ISO date string
  // Unified media payload - 1:1 correspondence with files[] array
  media: MediaInput[]
  files: File[]
  audioPosters?: File[] // Separate posters for audio files
  videoThumbnails?: File[] // Separate thumbnails for video files
  existingMedia?: Array<{ uploadId: string; isCover: boolean }>
  videos?: VideoInput[]
  courseModules?: CourseModuleInput[]
  isDraft?: boolean // true = draft, false = published
}

export interface CourseModuleInput {
  id?: string
  uploadId?: string
  title: string
  description: string
  duration: string
  order: number
  isFree: boolean
  videoFile?: File
  thumbnailFile?: File
  removeThumbnail?: boolean
}

export interface RitualResponse {
  id: string
  slug?: string
  title: string
  type: RitualType
  date: Date
  repeat: RepeatOption
  duration: number
  accessType: RitualVisibility // Matches backend field name
  communityId?: string
  createdAt: Date
}

/**
 * Create a new ritual
 * Supports multipart/form-data for file uploads
 */
export async function createRitual(data: CreateRitualRequest): Promise<RitualResponse> {
  const formData = new FormData()

  // Basic info
  formData.append("title", data.title)
  formData.append("type", data.type)
  formData.append("date", data.date)
  formData.append("repeat", data.repeat) // Send as-is (lowercase), controller will map to uppercase enum
  formData.append("duration", data.duration.toString())

  // Custom repeat fields
  if (data.repeatAt) formData.append("repeatAt", data.repeatAt)
  if (data.repeatAtType) formData.append("repeatAtType", data.repeatAtType)

  // Location (optional)
  if (data.streetAddress) formData.append("streetAddress", data.streetAddress)
  if (data.city) formData.append("city", data.city)
  if (data.state) formData.append("state", data.state)
  if (data.country) formData.append("country", data.country)
  if (data.lat !== undefined) formData.append("lat", data.lat.toString())
  if (data.long !== undefined) formData.append("long", data.long.toString())

  // Pricing
  formData.append("participationFee", data.participationFee)
  if (data.price !== undefined) formData.append("price", data.price.toString())
  if (data.feeDetails) formData.append("feeDetails", data.feeDetails)

  // Details (optional)
  if (data.description) formData.append("description", data.description)
  if (data.whatToBring) formData.append("whatToBring", data.whatToBring)
  if (data.experienceDetails) formData.append("experienceDetails", data.experienceDetails)
  if (data.intention) formData.append("intention", data.intention)
  if (data.intentionIds && data.intentionIds.length > 0)
    formData.append("intentionIds", JSON.stringify(data.intentionIds))
  if (data.meetingLink) formData.append("meetingLink", data.meetingLink)
  // Steps should be sent as a string (join array with newlines if needed)
  if (data.steps) {
    const stepsString = Array.isArray(data.steps) ? data.steps.join("\n") : data.steps
    formData.append("steps", stepsString)
  }
  if (data.webOrSocialMediaLink) formData.append("webOrSocialMediaLink", data.webOrSocialMediaLink)

  // Access control
  formData.append("accessType", data.accessType)
  if (data.communityId) formData.append("communityId", data.communityId)

  // Privacy settings - as JSON strings
  formData.append("whoCanSeeYourRitual", JSON.stringify(data.privacySettings.whoCanSeeYourRitual))
  formData.append("whoCanComment", JSON.stringify(data.privacySettings.whoCanComment))
  formData.append("whoCanEditRitual", JSON.stringify(data.privacySettings.whoCanEditRitual))
  formData.append("whoCanJoin", JSON.stringify(data.privacySettings.whoCanJoin))
  if (data.privacySettings.whoCanInvite) {
    formData.append("whoCanInvite", JSON.stringify(data.privacySettings.whoCanInvite))
  }
  if (data.privacySettings.whoCanSeeParticipantList) {
    formData.append(
      "whoCanSeeParticipantList",
      JSON.stringify(data.privacySettings.whoCanSeeParticipantList),
    )
  }

  // Course video access control
  if (data.courseVideoAccess) {
    formData.append("courseVideoAccess", data.courseVideoAccess)
  }

  // Minimum participant requirement
  if (data.minParticipantRequirement !== undefined) {
    formData.append(
      "activateMinimumParticipantRequirement",
      data.minParticipantRequirement.toString(),
    )
  }
  if (data.minParticipants !== undefined) {
    formData.append("minParticipants", data.minParticipants.toString())
  }
  if (data.registrationDeadline !== undefined) {
    formData.append("registrationDeadline", data.registrationDeadline)
  }

  // isDraft flag - send isDraft as boolean string (true = draft, false = published)
  if (data.isDraft !== undefined) {
    formData.append("isDraft", data.isDraft.toString())
  }

  // Tags - as JSON string with add format
  if (data.tags && data.tags.length > 0) {
    formData.append("tags", JSON.stringify({ add: data.tags }))
  } else {
    formData.append("tags", JSON.stringify({ add: [] }))
  }

  // Organizers - as JSON string
  if (data.organizers && data.organizers.length > 0) {
    formData.append("organizers", JSON.stringify(data.organizers))
  } else {
    formData.append("organizers", JSON.stringify([]))
  }

  // Unified media metadata array - 1:1 correspondence with files[]
  if (data.media && data.media.length > 0) {
    formData.append("media", JSON.stringify(data.media))
  } else {
    formData.append("media", JSON.stringify([]))
  }

  // Files (all media: images, videos, audio) - aligned with media[]
  if (data.files && data.files.length > 0) {
    data.files.forEach(file => {
      formData.append("files", file)
    })
  }

  // Audio posters (cover images for audio files) - indexed by media[].posterIndex
  if (data.audioPosters && data.audioPosters.length > 0) {
    data.audioPosters.forEach(file => {
      formData.append("audioPosters", file)
    })
  }

  // Video thumbnails (cover images for video files) - indexed by media[].videoThumbnailIndex
  // Field name matches backend middleware: 'media_thumbnails'
  if (data.videoThumbnails && data.videoThumbnails.length > 0) {
    data.videoThumbnails.forEach(file => {
      formData.append("media_thumbnails", file)
    })
  }

  // Existing media (from duplicate-to-relist — reuse existing uploads)
  if (data.existingMedia && data.existingMedia.length > 0) {
    formData.append("existingMedia", JSON.stringify(data.existingMedia))
  }

  // Course modules - as JSON string
  // Video files for course modules are appended separately as courseModuleVideos
  // uploadId is included for duplicate-to-relist flow to skip re-upload
  if (data.courseModules && data.courseModules.length > 0) {
    const modulesData = data.courseModules.map(m => ({
      title: m.title,
      description: m.description,
      duration: m.duration,
      order: m.order,
      isFree: m.isFree,
      uploadId: m.uploadId,
      ...(m.thumbnailFile ? { hasThumbnail: true } : {}),
    }))
    formData.append("courseModules", JSON.stringify(modulesData))

    // Append video files only for modules that don't have an existing uploadId
    data.courseModules.forEach(module => {
      if (module.videoFile && !module.uploadId) {
        formData.append("courseModuleVideos", module.videoFile)
      }
    })

    // Append thumbnail files for modules that have them
    data.courseModules.forEach(module => {
      if (module.thumbnailFile) {
        formData.append("courseModuleThumbnails", module.thumbnailFile)
      }
    })
  }

  // Videos - as JSON string
  if (data.videos && data.videos.length > 0) {
    formData.append("videos", JSON.stringify(data.videos))
  }

  const response = await apiClient.post<{
    success: boolean
    data: RitualResponse
    message: string
  }>("/api/rituals", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  })

  return response.data.data
}

// ========================================
// Types for Ritual Detail and Update
// ========================================

export interface RitualUpload {
  id: string
  fileName: string
  filePath: string
  fileType: string
  fileSize: number
  isCover: boolean
  // User-defined title for the media (shown in edit UI)
  title?: string | null
  // For audio rows, thumbnailPath holds the user-uploaded poster image.
  thumbnailPath?: string | null
  // Video thumbnail URL from separate thumbnailUpload
  thumbnailUrl?: string | null
  // Media type: IMAGE, VIDEO, AUDIO
  mediaType?: string
  // Sort order for display
  sortOrder?: number
  createdAt: string
  updatedAt: string
}

export type RitualVideoStatus = "pending" | "processing" | "ready" | "failed"

export interface RitualVideo {
  id: string
  ritualId: string
  // For Cloudflare-hosted rows (native pipeline, issue #362), videoUrl is null
  // and cloudflareUid is populated. For legacy YouTube/Vimeo rows, videoUrl
  // is the embeddable URL and cloudflareUid is null. The XOR is enforced at
  // the DB level when status=ready.
  videoUrl: string | null
  cloudflareUid: string | null
  status: RitualVideoStatus
  duration: number | null
  failureReason: string | null
  thumbnailUrl: string | null
  title: string | null
  sortOrder: number
  createdAt: string
  updatedAt: string
}

// -- Native video pipeline (issue #362) ------------------------------------

export interface RequestVideoUploadUrlInput {
  filename: string
  contentType: string
  sizeBytes: number
  durationSeconds: number
}

export interface RequestVideoUploadUrlResponse {
  signedUrl: string
  gcsPath: string
  contentType: string
  maxBytes: number
  expiresAt: number
}

export interface ConfirmVideoUploadInput {
  gcsPath: string
  sortOrder?: number
  title?: string | null
}

export interface ConfirmVideoUploadResponse {
  ritualVideoId: string
  status: RitualVideoStatus
}

export interface PlaybackTokenResponse {
  /** Signed JWT for PRIVATE/PERSONAL rituals; null for PUBLIC. */
  token: string | null
  /** Unix seconds; null when no token. */
  expiresAt: number | null
  /** Cloudflare Stream video UID. Always returned. */
  cloudflareUid: string
  /** Full HLS manifest URL (or null if customer subdomain not configured). */
  playbackUrl: string | null
}

export const requestVideoUploadUrl = async (
  ritualId: string,
  input: RequestVideoUploadUrlInput,
): Promise<RequestVideoUploadUrlResponse> => {
  const response = await apiClient.post<{
    success: boolean
    data: RequestVideoUploadUrlResponse
  }>(`/api/rituals/${ritualId}/videos/upload-url`, input, { timeout: 30000 })
  return response.data.data
}

export const confirmVideoUpload = async (
  ritualId: string,
  input: ConfirmVideoUploadInput,
): Promise<ConfirmVideoUploadResponse> => {
  const response = await apiClient.post<{
    success: boolean
    data: ConfirmVideoUploadResponse
  }>(`/api/rituals/${ritualId}/videos`, input, { timeout: 30000 })
  return response.data.data
}

export const getRitualVideoPlaybackToken = async (
  ritualId: string,
  videoId: string,
): Promise<PlaybackTokenResponse> => {
  const response = await apiClient.get<{
    success: boolean
    data: PlaybackTokenResponse
  }>(`/api/rituals/${ritualId}/videos/${videoId}/playback-token`)
  return response.data.data
}

export const deleteRitualVideo = async (ritualId: string, videoId: string): Promise<void> => {
  await apiClient.delete(`/api/rituals/${ritualId}/videos/${videoId}`)
}

/** One native ritual video row as projected by the backend list endpoint. */
export interface RitualVideoListItem {
  id: string
  ritualId: string
  status: RitualVideoStatus
  cloudflareUid: string | null
  videoUrl: string | null
  duration: number | null
  thumbnailUrl: string | null
  title: string | null
  sortOrder: number
  /** Only populated for the ritual owner. */
  failureReason: string | null
  createdAt: string
  updatedAt: string
}

/**
 * List the native (Cloudflare-hosted) videos for a ritual. Used on the
 * edit page so owners can see + remove existing videos -- the uploader's
 * local state only tracks this-session uploads, and the legacy media grid
 * reads the separate uploads table.
 */
export const listRitualVideos = async (ritualId: string): Promise<RitualVideoListItem[]> => {
  const response = await apiClient.get<{
    success: boolean
    data: RitualVideoListItem[]
    message: string
  }>(`/api/rituals/${ritualId}/videos`)
  return response.data.data
}

/**
 * Update a native ritual video's thumbnail and/or title.
 * PATCH /api/rituals/:ritualId/videos/:videoId
 */
export const updateRitualVideo = async (
  ritualId: string,
  videoId: string,
  data: { title?: string; thumbnail?: File },
): Promise<{ id: string; title: string | null; thumbnailUrl: string | null }> => {
  const formData = new FormData()
  if (data.title !== undefined) formData.append("title", data.title)
  if (data.thumbnail) formData.append("thumbnail", data.thumbnail)

  const response = await apiClient.patch<{
    success: boolean
    data: { id: string; title: string | null; thumbnailUrl: string | null }
  }>(`/api/rituals/${ritualId}/videos/${videoId}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  })
  return response.data.data
}

export interface RitualTag {
  id: string
  name: string
  model: string
  modelId: string
  createdAt: string
  updatedAt: string
}

export interface RitualSetting {
  id: string
  ritualId: string
  whoCanSeeYourRitual: string[]
  whoCanComment: string[]
  whoCanEditRitual: string[]
  whoCanJoin: string[]
  updatedAt: string
}

export interface RitualOrganiserUser {
  id: string
  username: string | null
  name: string | null
  profilePictureUrl: string | null
  coverImage: string | null
  isFollowing?: boolean
}

export interface RitualOrganiserCommunity {
  id: string
  name: string
  handle: string
  coverImage: string | null
  isMember?: boolean
}

export interface RitualOrganiser {
  id: string
  type: "user" | "community" // API returns 'type', not 'organiserType'
  organiserId: string
  // Populated details
  user?: RitualOrganiserUser | null
  community?: RitualOrganiserCommunity | null
}

export interface RitualCourseModule {
  id: string
  title: string
  description: string | null
  duration: string | null
  orderIndex: number
  isFree: boolean
  thumbnailUrl?: string | null
  upload?: {
    id?: string
    filePath: string
    fileName: string
    fileType?: string
    thumbnailUrl?: string | null
    mediaType?: string
  }
  // User progress for this module (if authenticated)
  // Note: The API returns minutesWatched field but it contains seconds (for precision)
  userProgress?: Array<{
    id: string
    minutesWatched: number // This contains seconds in the database
    status: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED"
    completedAt: string | null
  }>
}

export interface RitualDetail {
  id: string
  slug?: string
  title: string
  // API returns these with different names
  ritualType: RitualType
  ritualTs: Date
  repeat: string
  repeatAt?: string | null
  repeatAtType?: "DAYS" | "HOURS" | "DATE" | null
  duration: number
  streetAddress?: string
  city?: string
  state?: string
  country?: string
  lat?: number
  long?: number
  participationFee: ParticipationFee
  price?: number
  fee?: string
  feeDetails?: string
  isPaid: boolean
  isDonationBased?: boolean
  courseVideoAccess?: "ALL" | "SEQUENTIAL"
  userDonationAmount?: number | null
  description?: string
  whatToBring?: string
  experienceDetails?: string
  intention?: string
  intentionIds?: string[]
  intentions?: Array<{ id: string; name: string }>
  steps?: string
  meetingLink?: string | null
  websiteLink?: string | null
  webOrSocialMediaLink?: string // Frontend form field name, maps to websiteLink
  accessType: RitualVisibility // Matches backend field name
  communityId?: string
  createdBy?: string | null
  deleted: boolean
  activateMinimumParticipantRequirement?: boolean | null
  minParticipants?: number | null
  registrationDeadline?: Date | string | null
  createdAt: Date
  updatedAt: Date
  updatedByUser?: { id: string; name: string | null; email: string | null } | null
  // Privacy settings - JSON strings from API that need to be parsed
  whoCanSeeYourRitual?: string // JSON string array, needs parseJsonArray
  whoCanComment?: string[] // Backend returns parsed array (now)
  whoCanEditRitual?: string // JSON string array, needs parseJsonArray
  whoCanJoin?: string[] // Backend returns parsed array
  whoCanInvite?: string // JSON string array, needs parseJsonArray
  whoCanSeeParticipantList?: string // JSON string array, needs parseJsonArray
  // Associations
  settings?: RitualSetting
  organisers?: RitualOrganiser[]
  tags?: (RitualTag & { tagText?: string })[] // API includes tagText
  uploads?: RitualUpload[]
  courseModules?: RitualCourseModule[]
  videos?: RitualVideo[]
  isSaved?: boolean
  isOwner?: boolean
  isOrganizer?: boolean
  canEdit?: boolean
  canInvite?: boolean
  hasPendingRequest?: boolean
  membershipDetails?: RitualMembershipDetails | null
  discussionPermissions?: {
    canComment: boolean
    canReply: boolean
  }
  // Self-guided ritual progress
  completedDays?: number
  canStartRitual?: boolean
  currentProgressStatus?: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED" | null
  currentProgressId?: string | null
  // Course progress (for SELF_GUIDED_COURSE)
  courseProgress?: {
    completedModules: number
    totalModules: number
    percentage: number
    showContinueCourse?: boolean
    showStartCourse?: boolean
  }
  // Sample members
  sampleMembers?: {
    name: string
    username: string
    profileCoverImage: string | null
  }[]
  // Participant stats
  joinedMembersCount?: number
  savedCount?: number
  viewsCount?: number
  completedCount?: number
  // Live status
  isLive?: boolean
  // Admin completion
  isAdminCompleted?: boolean
  completedAt?: string | null
  // Computed completion status (matches listing API: is_admin_completed OR date passed)
  isCompleted?: boolean
  // Computed status flags for non-admin visibility
  canceled?: boolean
  completed?: boolean
  // Archive
  isArchived?: boolean
  archivedAt?: string | null
  // Default / onboarding demo ritual
  isDefault?: boolean
  metadata?: Record<string, unknown> | null
  // Cancellation
  canceledAt?: string | null
  canceledReason?: string | null
  canceledBy?: string | null
  // Refund request
  refundRequest?: {
    hasRequest: boolean
    status?: "PENDING" | "APPROVED" | "REJECTED" | "REFUNDED"
    requestId?: string
  }
  // Ritual earnings (owner/organizer only)
  ritualEarnings?: {
    totalEarnings: number
    paidParticipantCount?: number
    perParticipantAmount?: number
  } | null
}

export interface UpdateRitualData {
  title?: string
  type?: RitualType
  accessType?: RitualVisibility
  date?: string
  repeat?: string
  repeatAt?: string
  repeatAtType?: "DAYS" | "HOURS" | "DATE"
  duration?: number
  streetAddress?: string
  city?: string
  state?: string
  country?: string
  lat?: number
  long?: number
  participationFee?: ParticipationFee
  courseVideoAccess?: "ALL" | "SEQUENTIAL"
  price?: number
  feeDetails?: string
  description?: string
  whatToBring?: string
  experienceDetails?: string
  intention?: string
  intentionIds?: string[]
  meetingLink?: string
  steps?: string
  webOrSocialMediaLink?: string
  communityId?: string
  tags?: { add?: string[]; remove?: string[] }
  organizers?: {
    add?: Array<{ type: "user" | "community"; id: string; inviteNote?: string }>
    remove?: string[]
  }
  activateMinimumParticipantRequirement?: boolean
  minParticipants?: number
  registrationDeadline?: string
  courseModules?: Array<{
    id?: string
    uploadId?: string
    title: string
    description: string
    duration: string
    order: number
    isFree: boolean
    videoFile?: File
    thumbnailFile?: File
    removeThumbnail?: boolean
  }>
  whoCanSeeYourRitual?: string[]
  whoCanComment?: string[]
  whoCanEditRitual?: string[]
  whoCanJoin?: string[]
  whoCanInvite?: string[]
  mediaChanges?: {
    add?: {
      media?: MediaInput[]
      files?: File[]
      audioPosters?: File[]
      videoThumbnails?: File[]
    }
    delete?: string[]
    reorder?: string[]
    setCover?: string
  }
  whoCanSeeParticipantList?: string[]
  videos?: VideoChanges
  /**
   * Title updates for existing media items (videos/audios).
   * Backend needs to process this separately from mediaChanges.add.
   */
  mediaTitleUpdates?: Array<{
    id: string
    title: string
    mediaType: string
  }>
}

// ========================================
// GET Ritual by ID
// ========================================

/**
 * Add a joined ritual to the user's connected Google Calendar.
 */
export async function addRitualToCalendar(ritualId: string): Promise<{
  syncStatus: "synced" | "pending" | "failed"
  htmlLink?: string
  googleEventId?: string
}> {
  const response = await apiClient.post<{
    success: boolean
    data: {
      syncStatus: "synced" | "pending" | "failed"
      htmlLink?: string
      googleEventId?: string
    }
    message: string
  }>(`/api/rituals/${ritualId}/add-to-calendar`)
  return response.data.data
}

/**
 * Get ritual by ID with all associations
 * Note: Backend already returns camelCase (Sequelize with underscored: true)
 */
export async function getRitualById(id: string): Promise<RitualDetail> {
  const response = await apiClient.get<{
    success: boolean
    data: RitualDetail
    message: string
  }>(`/api/rituals/${id}`)

  return response.data.data as RitualDetail
}

/**
 * Get ritual organizers with pagination
 *
 * @param ritualId - Ritual ID
 * @param page - Page number (default: 1)
 * @param limit - Items per page (default: 20)
 * @returns Organizers with pagination info
 */
export async function getRitualOrganizers(
  ritualId: string,
  page: number = 1,
  limit: number = 20,
  status?: string,
): Promise<RitualOrganizersResponse> {
  const response = await apiClient.get<{
    success: boolean
    data: RitualOrganizersResponse
    message: string
  }>(`/api/rituals/${ritualId}/organizers`, {
    params: { page, limit, status },
  })

  return response.data.data
}

/**
 * Get completed days for a ritual by month
 *
 * @param ritualId - Ritual ID
 * @param month - Month (1-12)
 * @param year - Year (YYYY)
 * @returns Array of date strings (e.g., ["2026-04-04", "2026-04-05", "2026-04-06"])
 */
export async function getCompletedDaysByMonth(
  ritualId: string,
  month: number,
  year: number,
): Promise<string[]> {
  const response = await apiClient.get<{
    success: boolean
    data: { completedDates: string[] }
    message: string
  }>(`/api/rituals/${ritualId}/completed-days`, {
    params: { month, year },
  })

  return response.data.data.completedDates
}

/**
 * Transform API organisers to component-friendly format
 * Converts enriched organisers with user/community objects to flat structure
 */
export function transformOrganizers(organisers: RitualOrganiser[] | undefined) {
  if (!organisers) return []

  return organisers.map(org => ({
    id: org.id,
    organiserType: org.type,
    organiserId: org.organiserId,
    user: org.user
      ? {
          ...org.user,
          isFollowing: org.user.isFollowing ?? false,
          followerCount: (org.user as unknown as { followerCount?: number }).followerCount ?? 0,
        }
      : null,
    community: org.community
      ? {
          ...org.community,
          isMember: org.community.isMember ?? false,
          totalMemberCount:
            (org.community as unknown as { totalMemberCount?: number }).totalMemberCount ?? 0,
        }
      : null,
    name: org.user?.name || org.community?.name || "Unknown",
    avatar:
      org.user?.coverImage || org.community?.coverImage || org.user?.profilePictureUrl || undefined,
    followers: 0,
  }))
}

// Convert regular URL to embed URL for YouTube/Vimeo
export function getEmbedUrl(url: string): string {
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/)
  if (ytMatch)
    return `https://www.youtube.com/embed/${ytMatch[1]}?controls=0&showinfo=0&rel=0&modestbranding=1`

  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/)
  if (vimeoMatch)
    return `https://player.vimeo.com/video/${vimeoMatch[1]}?badge=0&byline=0&portrait=0&title=0&controls=0`

  return url
}

/**
 * Transform API RitualDetail to UI RitualDetailData format
 * Maps backend response to the structure expected by UI components
 */
export function transformRitualDetailToUI(apiRitual: RitualDetail): RitualDetailData {
  // Map type to format using centralized mapping
  const format: RitualFormat = getRitualTypeFormat(apiRitual.ritualType) as RitualFormat

  // Transform uploads to media — three discriminants based on fileType
  // (or file extension as a fallback for older rows that may be missing it).
  const uploadMedia =
    apiRitual.uploads?.map(upload => {
      // optional-chained .match() returns undefined when filePath is missing,
      // and undefined !== null is true — coerce to a boolean instead of
      // comparing to null so a missing filePath doesn't masquerade as audio.
      const isAudio =
        upload.fileType?.startsWith("audio/") ||
        Boolean(upload.filePath?.match(/\.(mp3|wav|m4a|aac|ogg|oga)$/i))
      const isVideo =
        !isAudio &&
        (upload.fileType?.startsWith("video/") ||
          Boolean(upload.filePath?.match(/\.(mp4|webm|mov)$/i)))

      return {
        id: upload.id,
        url: upload.filePath,
        type: (isAudio ? "audio" : isVideo ? "video" : "image") as "image" | "video" | "audio",
        isCover: upload.isCover,
        sortOrder: upload.sortOrder ?? 0,
        // For audio and video, surface the user-uploaded poster as thumbnailUrl
        // so the hero player and course-list row can render it without re-fetching.
        thumbnailUrl: isAudio || isVideo ? (upload.thumbnailUrl ?? undefined) : undefined,
      }
    }) || []

  // Transform videos to media items. Each row is one of:
  //   - Legacy YouTube/Vimeo: videoUrl set, cloudflareUid null. url is the
  //     getEmbedUrl-transformed iframe URL.
  //   - Native (Cloudflare Stream, issue #362): videoUrl null, cloudflareUid
  //     set. url stays "" since the player picks it up from cloudflareUid +
  //     a JWT (for private/personal rituals) fetched at render time.
  // Both shapes flow through the same RitualMedia type; the carousel
  // discriminates on cloudflareUid to pick the right player.
  //
  // Filter out non-playable rows (native videos still pending/processing/
  // failed). Without this, a failed row falls through to the legacy
  // iframe branch in RitualMediaCarousel with src="&autoplay=1", which
  // the browser resolves as a relative URL inside the React app -- the
  // detail-page query starts firing with ":ritualId = &autoplay=1" and
  // the backend returns 500 in a loop. The ritual owner still sees
  // failed rows in the edit-page uploader; the public carousel only
  // gets the playable ones.
  const videoMedia =
    apiRitual.videos
      ?.filter(video => {
        // Legacy YouTube/Vimeo: a videoUrl is sufficient to play. The
        // DB CHECK constraint guarantees XOR with cloudflareUid for
        // ready rows, so this only matches legacy embeds.
        if (video.videoUrl) return true
        // Native (issue #362): must be transcoded + ingested by
        // Cloudflare. Pending/processing/failed rows have no playable
        // source yet -- exclude them from the public carousel so a
        // half-baked row can't fall through to the iframe branch with
        // an empty url (which the browser resolves as a relative URL
        // and triggers an infinite 500 refetch loop on getRitualById).
        return video.status === "ready" && video.cloudflareUid !== null
      })
      .map(video => ({
        id: video.id,
        url: video.videoUrl ? getEmbedUrl(video.videoUrl) : "",
        type: "video" as const,
        isCover: false,
        // sortOrder lets the combined media list interleave videos with
        // images/audio under the drag-sort redesign (PR #423).
        sortOrder: video.sortOrder,
        thumbnailUrl: video.thumbnailUrl || undefined,
        title: video.title || undefined,
        cloudflareUid: video.cloudflareUid,
        cloudflareStatus: video.status,
        cloudflareFailureReason: video.failureReason,
        cloudflareDuration: video.duration,
      })) || []

  // Combine uploads and videos, sort by sortOrder
  const allMedia = [...uploadMedia, ...videoMedia].sort((a, b) => {
    const sortA = a.sortOrder ?? 0
    const sortB = b.sortOrder ?? 0
    return sortA - sortB
  })

  // Ensure cover image is always first in the carousel
  const coverIndex = allMedia.findIndex(m => m.type === "image" && m.isCover)
  if (coverIndex > 0) {
    const [cover] = allMedia.splice(coverIndex, 1)
    allMedia.unshift(cover)
  }
  const media = allMedia

  // For default/onboarding rituals with no real media, construct a synthetic
  // cover image from metadata.demoData.coverImageAsset
  if (apiRitual.isDefault && media.length === 0) {
    const demoData = (apiRitual.metadata as Record<string, unknown> | null)?.demoData as
      | { coverImageAsset?: string }
      | undefined
    const coverAsset = demoData?.coverImageAsset
    if (coverAsset) {
      media.push({
        id: `default-cover-${apiRitual.id}`,
        url: getAssetUrl(coverAsset),
        type: "image" as const,
        isCover: true,
        sortOrder: 0,
        thumbnailUrl: undefined,
      })
    }
  }

  // Transform tags - use tagText from API, filter out null/undefined
  const tags =
    apiRitual.tags?.map(tag => tag.tagText).filter((tag): tag is string => tag != null) || []

  // Transform organisers
  const organizers = transformOrganizers(apiRitual.organisers)

  // Build location string
  const locationParts = [apiRitual.streetAddress, apiRitual.city, apiRitual.state].filter(Boolean)
  const location = locationParts.length > 0 ? locationParts.join(", ") : undefined

  // Parse date and time from ritualTs
  const dateObj = new Date(apiRitual.ritualTs)
  const date = dateObj.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  })
  const time = dateObj.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  })

  // Transform whatToBring and experienceDetails from string to array
  const whatYoullNeed = apiRitual.whatToBring
    ? apiRitual.whatToBring.split("\n").filter(line => line.trim())
    : undefined

  const whatYoullExperience = apiRitual.experienceDetails
    ? apiRitual.experienceDetails.split("\n").filter(line => line.trim())
    : undefined

  // Transform steps to stepByStepGuidance for self-guided rituals
  const stepByStepGuidance = apiRitual.steps
    ? apiRitual.steps
        .split("\n")
        .filter(line => line.trim())
        .map((step, index) => {
          // Parse each line - could be "Title - Description" or just plain text
          const parts = step.split(/[-–—]/) // Split on dash variants
          const title = parts[0]?.trim() || `Step ${index + 1}`
          const description = parts.slice(1).join("-").trim() || ""

          return {
            number: index + 1,
            title,
            description: description || title,
          }
        })
    : undefined

  // Helper function to parse duration string to seconds
  // Supports formats: "31 sec", "5 min", "10:30" (10 min 30 sec), "15" (assumes minutes)
  const parseDurationToSeconds = (durationStr: string | null | undefined): number => {
    if (!durationStr) return 0

    // Format: "10:30" (minutes:seconds)
    if (durationStr.includes(":")) {
      const parts = durationStr.split(":")
      const minutes = parseInt(parts[0] || "0", 10)
      const seconds = parseInt(parts[1] || "0", 10)
      return minutes * 60 + seconds
    }

    // Format: "31 sec", "5 min", "10 minutes", "30 seconds"
    const lowerStr = durationStr.toLowerCase()

    // Check for seconds first
    if (lowerStr.includes("sec")) {
      const match = durationStr.match(/(\d+)\s*(sec|second|seconds)?/i)
      if (match) {
        return parseInt(match[1], 10)
      }
    }

    // Check for minutes
    if (lowerStr.includes("min")) {
      const match = durationStr.match(/(\d+)\s*(min|minute|minutes)?/i)
      if (match) {
        return parseInt(match[1], 10) * 60
      }
    }

    // Format: just a number (assume minutes)
    const num = parseInt(durationStr, 10)
    return Number.isFinite(num) ? num * 60 : 0
  }

  // Transform course modules with progress tracking and sequential unlocking
  const courseModules =
    apiRitual.courseModules
      ?.map(m => {
        const totalSeconds = parseDurationToSeconds(m.duration)
        const userProgress = m.userProgress?.[0] // Get first (and should be only) progress record

        // The API returns minutesWatched field but it contains seconds (for precision)
        const secondsWatched = userProgress?.minutesWatched || 0
        const progressPercent =
          totalSeconds > 0 ? Math.min(Math.round((secondsWatched / totalSeconds) * 100), 100) : 0

        return {
          id: m.id,
          title: m.title,
          description: m.description || "",
          duration: m.duration || undefined,
          order: m.orderIndex,
          isFree: m.isFree,
          videoUrl: m.upload?.filePath,
          fileType: m.upload?.fileType,
          thumbnailUrl: m.thumbnailUrl ?? undefined,
          progressSeconds: secondsWatched,
          progressPercent,
          progressStatus: userProgress?.status || "NOT_STARTED",
        }
      })
      .sort((a, b) => a.order - b.order) // First sort by order
      .map((module, index, sortedModules) => {
        // Calculate if this module should be unlocked based on sequential completion
        let isUnlocked = false

        if (index === 0) {
          // First module is always unlocked
          isUnlocked = true
        } else {
          // Check if previous module is completed
          const prevModule = sortedModules[index - 1]
          isUnlocked = prevModule?.progressStatus === "COMPLETED"
        }

        return {
          ...module,
          isUnlocked,
        }
      }) || []

  return {
    id: apiRitual.id,
    slug: apiRitual.slug,
    title: apiRitual.title,
    accessType: apiRitual.accessType,
    description: apiRitual.description || "",
    format,
    ritualType: apiRitual.ritualType,
    fee: apiRitual.isDonationBased ? "donation" : apiRitual.isPaid ? `$${apiRitual.fee}` : "free",
    isPaid: apiRitual.isPaid ?? false,
    isDonationBased: apiRitual.isDonationBased ?? false,
    userDonationAmount: apiRitual.userDonationAmount ?? null,
    // Determine visibility from privacy settings - PERSONAL if whoCanSeeYourRitual is ["only_me"]
    visibility: apiRitual.whoCanSeeYourRitual?.includes("only_me") ? "PERSONAL" : undefined,
    price:
      apiRitual.fee != null
        ? Number.isFinite(+apiRitual.fee)
          ? +apiRitual.fee
          : undefined
        : undefined,
    duration: `${apiRitual.duration}`,
    date,
    time,
    repeat:
      apiRitual.repeat?.toUpperCase() !== "NONE" ? apiRitual.repeat?.toLowerCase() : undefined,
    location,
    meetingLink: apiRitual.meetingLink ?? undefined,
    websiteLink: apiRitual.websiteLink ?? undefined,
    platform: undefined, // Not currently in API
    media,
    whatYoullNeed,
    whatYoullExperience,
    stepByStepGuidance,
    intention: apiRitual.intention,
    intentions: (apiRitual.intentions || []) as Array<{ id: string; name: string }>,
    tags,
    organizers,
    isOrganizer: apiRitual.isOrganizer ?? false,
    isOwner: apiRitual.isOwner ?? false,
    canEdit: apiRitual.canEdit ?? false,
    canInvite: apiRitual.canInvite ?? false,
    whoCanJoin: apiRitual.whoCanJoin ?? [],
    whoCanComment: apiRitual.whoCanComment ?? [],
    whoCanSeeParticipantList: (() => {
      const raw = apiRitual.whoCanSeeParticipantList
      if (!raw) return undefined
      if (Array.isArray(raw)) return raw as string[]
      try {
        return JSON.parse(raw) as string[]
      } catch {
        return undefined
      }
    })(),
    hasPendingRequest: apiRitual.hasPendingRequest ?? false,
    isSaved: apiRitual.isSaved ?? false,
    membershipDetails: apiRitual.membershipDetails ?? undefined,
    discussionPermissions: apiRitual.discussionPermissions,
    comments: [], // TODO: Fetch from comments API
    createdBy: apiRitual.createdBy,
    updatedAt: apiRitual.updatedAt ? apiRitual.updatedAt.toString() : null,
    updatedBy: apiRitual.updatedByUser ?? null,
    communityId: apiRitual.communityId,
    participantStats: {
      joined: apiRitual.joinedMembersCount ?? 0,
      saved: apiRitual.savedCount ?? 0,
      views: apiRitual.viewsCount ?? 0,
      completed: apiRitual.completedCount ?? 0,
      avatars: apiRitual.isDefault
        ? (apiRitual.sampleMembers || [])
            .map(m => (m.profileCoverImage ? getAssetUrl(m.profileCoverImage) : ""))
            .filter(Boolean)
        : (apiRitual.sampleMembers?.map(m => m.profileCoverImage).filter(Boolean) as string[]) ||
          [],
    },
    sampleMembers: apiRitual.isDefault
      ? (apiRitual.sampleMembers || []).map(m => ({
          ...m,
          profileCoverImage: m.profileCoverImage ? getAssetUrl(m.profileCoverImage) : null,
        }))
      : apiRitual.sampleMembers,
    courseModules,
    courseVideoAccess: apiRitual.courseVideoAccess ?? undefined,
    // Minimum participant requirement
    activateMinimumParticipantRequirement:
      apiRitual.activateMinimumParticipantRequirement ?? undefined,
    minParticipants: apiRitual.minParticipants ?? undefined,
    registrationDeadline: apiRitual.registrationDeadline ?? undefined,
    // Self-guided ritual progress
    completedDays: apiRitual.completedDays ?? 0,
    canStartRitual: apiRitual.canStartRitual ?? true,
    currentProgressStatus: apiRitual.currentProgressStatus ?? null,
    currentProgressId: apiRitual.currentProgressId ?? null,
    // Course progress (for SELF_GUIDED_COURSE)
    courseProgress: apiRitual.courseProgress,
    // Live status
    isLive: apiRitual.isLive ?? false,
    // Admin completion
    isAdminCompleted: apiRitual.isAdminCompleted ?? false,
    completedAt: apiRitual.completedAt ?? null,
    // Computed completion status (matches listing API: is_admin_completed OR date passed)
    isCompleted: apiRitual.isCompleted ?? false,
    // Computed status flags for non-admin visibility
    canceled: apiRitual.canceled ?? false,
    completed: apiRitual.completed ?? false,
    // Archive
    isArchived: apiRitual.isArchived ?? false,
    archivedAt: apiRitual.archivedAt ?? null,
    // Cancellation
    canceledAt: apiRitual.canceledAt ?? null,
    canceledReason: apiRitual.canceledReason ?? null,
    canceledBy: apiRitual.canceledBy ?? null,
    // Refund request
    refundRequest: apiRitual.refundRequest,
    // Ritual earnings (owner/organizer only)
    ritualEarnings: apiRitual.ritualEarnings ?? null,
    metadata: apiRitual.metadata ?? null,
  }
}

// ========================================
// UPDATE Ritual
// ========================================

/**
 * Update ritual with partial data
 * Note: Backend already returns camelCase (Sequelize with underscored: true)
 */
export async function updateRitual(
  id: string,
  data: Partial<UpdateRitualData> | FormData,
): Promise<RitualDetail> {
  // If data is already FormData, send it directly
  if (data instanceof FormData) {
    const response = await apiClient.patch<{
      success: boolean
      data: RitualDetail
      message: string
    }>(`/api/rituals/${id}`, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    return response.data.data as RitualDetail
  }

  // Check if we have course modules with video files - use FormData
  const hasCourseModulesWithFiles = data.courseModules?.some(m => m.videoFile)

  // Check if we have course module thumbnail files - use FormData
  const hasCourseModuleThumbnails = data.courseModules?.some(m => m.thumbnailFile)

  // Check if we have thumbnail changes for existing media - use FormData
  const hasMediaThumbnailChanges =
    data.mediaChanges?.add?.videoThumbnails && data.mediaChanges.add.videoThumbnails.length > 0

  // Check if we have new media files to upload - use FormData
  const hasNewMediaFiles = data.mediaChanges?.add?.files && data.mediaChanges.add.files.length > 0

  if (
    hasCourseModulesWithFiles ||
    hasCourseModuleThumbnails ||
    hasMediaThumbnailChanges ||
    hasNewMediaFiles
  ) {
    // Use FormData for file uploads
    const formData = new FormData()

    // Basic fields
    if (data.title !== undefined) formData.append("title", data.title)
    if (data.type !== undefined) formData.append("type", data.type)
    if (data.date !== undefined) formData.append("date", data.date)
    if (data.repeat !== undefined) formData.append("repeat", data.repeat)
    if (data.repeatAt !== undefined) formData.append("repeatAt", data.repeatAt)
    if (data.repeatAtType !== undefined) formData.append("repeatAtType", data.repeatAtType)
    if (data.duration !== undefined) formData.append("duration", data.duration.toString())
    if (data.streetAddress !== undefined) formData.append("streetAddress", data.streetAddress)
    if (data.city !== undefined) formData.append("city", data.city)
    if (data.state !== undefined) formData.append("state", data.state)
    if (data.country !== undefined) formData.append("country", data.country)
    if (data.lat !== undefined) formData.append("lat", data.lat.toString())
    if (data.long !== undefined) formData.append("long", data.long.toString())
    if (data.participationFee !== undefined)
      formData.append("participationFee", data.participationFee)
    if (data.courseVideoAccess !== undefined) {
      formData.append("courseVideoAccess", data.courseVideoAccess)
    }
    if (data.price !== undefined) formData.append("price", data.price.toString())
    if (data.feeDetails !== undefined) formData.append("feeDetails", data.feeDetails)
    if (data.description !== undefined) formData.append("description", data.description)
    if (data.whatToBring !== undefined) formData.append("whatToBring", data.whatToBring)
    if (data.experienceDetails !== undefined)
      formData.append("experienceDetails", data.experienceDetails)
    if (data.intention !== undefined) formData.append("intention", data.intention)
    if (data.intentionIds !== undefined)
      formData.append("intentionIds", JSON.stringify(data.intentionIds))
    if (data.meetingLink !== undefined) formData.append("meetingLink", data.meetingLink)
    if (data.steps !== undefined) formData.append("steps", data.steps)
    if (data.webOrSocialMediaLink !== undefined)
      formData.append("webOrSocialMediaLink", data.webOrSocialMediaLink)
    if (data.accessType !== undefined) formData.append("accessType", data.accessType)

    // Minimum participant requirement
    if (data.activateMinimumParticipantRequirement !== undefined) {
      formData.append(
        "activateMinimumParticipantRequirement",
        data.activateMinimumParticipantRequirement.toString(),
      )
    }
    if (data.minParticipants !== undefined) {
      formData.append("minParticipants", data.minParticipants.toString())
    }
    if (data.registrationDeadline !== undefined) {
      formData.append("registrationDeadline", data.registrationDeadline)
    }

    // Privacy settings
    if (data.whoCanSeeYourRitual !== undefined)
      formData.append("whoCanSeeYourRitual", JSON.stringify(data.whoCanSeeYourRitual))
    if (data.whoCanComment !== undefined)
      formData.append("whoCanComment", JSON.stringify(data.whoCanComment))
    if (data.whoCanEditRitual !== undefined)
      formData.append("whoCanEditRitual", JSON.stringify(data.whoCanEditRitual))
    if (data.whoCanJoin !== undefined)
      formData.append("whoCanJoin", JSON.stringify(data.whoCanJoin))

    // Tags
    if (data.tags !== undefined) {
      formData.append("tags", JSON.stringify(data.tags))
    }

    // Organizers
    if (data.organizers !== undefined) {
      formData.append("organizers", JSON.stringify(data.organizers))
    }

    // Course modules - as JSON string (send even if empty to signal deletion)
    if (data.courseModules !== undefined) {
      const modulesData = data.courseModules.map(m => ({
        id: m.id?.startsWith("new-") ? undefined : m.id,
        uploadId: m.uploadId,
        title: m.title,
        description: m.description,
        duration: m.duration,
        order: m.order,
        isFree: m.isFree,
        ...(m.thumbnailFile ? { hasThumbnail: true } : {}),
        ...(m.removeThumbnail ? { removeThumbnail: true } : {}),
      }))
      formData.append("courseModules", JSON.stringify(modulesData))

      // Append video files for each module
      // Important: New modules (no id, no uploadId) get files assigned in order
      data.courseModules.forEach(module => {
        if (module.videoFile) {
          formData.append("courseModuleVideos", module.videoFile)
        }
      })

      // Append thumbnail files for modules that have them
      data.courseModules.forEach(module => {
        if (module.thumbnailFile) {
          formData.append("courseModuleThumbnails", module.thumbnailFile)
        }
      })
    }

    // Videos - as JSON string
    if (data.videos !== undefined) {
      formData.append("videos", JSON.stringify(data.videos))
    }

    // Media title updates for existing media items
    if (data.mediaTitleUpdates !== undefined) {
      formData.append("mediaTitleUpdates", JSON.stringify(data.mediaTitleUpdates))
    }

    // Media changes with thumbnails for existing media
    if (data.mediaChanges !== undefined) {
      // Append new media files first (must happen before mediaChanges JSON)
      // Backend expects files to be indexed in order with media[] entries
      if (data.mediaChanges.add?.files) {
        data.mediaChanges.add.files.forEach(file => {
          formData.append("files", file)
        })
      }

      // Send media metadata as JSON
      const mediaChangesForJson = {
        add: {
          media: data.mediaChanges.add?.media,
          audioPosters: undefined, // Will be appended as files
          videoThumbnails: undefined, // Will be appended as files
        },
        delete: data.mediaChanges.delete,
        reorder: data.mediaChanges.reorder,
        setCover: data.mediaChanges.setCover,
      }
      formData.append("mediaChanges", JSON.stringify(mediaChangesForJson))

      // Append thumbnail files separately
      if (data.mediaChanges.add?.videoThumbnails) {
        data.mediaChanges.add.videoThumbnails.forEach(file => {
          formData.append("media_thumbnails", file)
        })
      }

      if (data.mediaChanges.add?.audioPosters) {
        data.mediaChanges.add.audioPosters.forEach(file => {
          formData.append("audioPosters", file)
        })
      }
    }

    const response = await apiClient.patch<{
      success: boolean
      data: RitualDetail
      message: string
    }>(`/api/rituals/${id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })

    return response.data.data as RitualDetail
  }

  // No file uploads - use JSON
  // Convert courseModules array to JSON string for backend validator compatibility
  const jsonData: Record<string, unknown> = { ...data }
  if (data.courseModules && Array.isArray(data.courseModules)) {
    jsonData.courseModules = JSON.stringify(data.courseModules) as unknown
  }
  // Stringify videos for backend
  if (data.videos !== undefined) {
    jsonData.videos = JSON.stringify(data.videos)
  }
  // Stringify intentionIds for backend validator compatibility
  if (data.intentionIds !== undefined) {
    jsonData.intentionIds = JSON.stringify(data.intentionIds)
  }

  // Stringify mediaTitleUpdates for backend
  if (data.mediaTitleUpdates !== undefined) {
    jsonData.mediaTitleUpdates = JSON.stringify(data.mediaTitleUpdates)
  }

  const response = await apiClient.patch<{
    success: boolean
    data: RitualDetail
    message: string
  }>(`/api/rituals/${id}`, jsonData)

  return response.data.data as RitualDetail
}

// ========================================
// Media Management
// ========================================

/**
 * Upload ritual image
 * Note: Backend already returns camelCase (Sequelize with underscored: true)
 */
export async function uploadRitualImage(
  ritualId: string,
  file: File,
): Promise<{ id: string; filePath: string; isCover: boolean }> {
  const formData = new FormData()
  formData.append("file", file)

  const response = await apiClient.post<{
    success: boolean
    data: { id: string; filePath: string; isCover: boolean }
    message: string
  }>(`/api/rituals/${ritualId}/images`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  })

  return response.data.data
}

/**
 * Set the cover image (poster) on an existing ritual upload. Used in the
 * edit flow to attach a cover image to an audio row after the audio is
 * already uploaded. Hits the dedicated cover-on-upload endpoint, not the
 * gallery cover-toggle endpoint.
 */
export async function setRitualUploadCover(
  ritualId: string,
  uploadId: string,
  file: File,
): Promise<{ id: string; thumbnailPath: string | null }> {
  const formData = new FormData()
  formData.append("file", file)

  const response = await apiClient.post<{
    success: boolean
    data: { id: string; thumbnailPath: string | null }
    message: string
  }>(`/api/rituals/${ritualId}/uploads/${uploadId}/cover`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  })

  return response.data.data
}

/**
 * Delete ritual image
 */
export async function deleteRitualImage(ritualId: string, uploadId: string): Promise<void> {
  await apiClient.delete(`/api/rituals/${ritualId}/images/${uploadId}`)
}

export async function replaceRitualImage(
  ritualId: string,
  uploadId: string,
  file: File,
): Promise<{ id: string; filePath: string; isCover: boolean }> {
  const formData = new FormData()
  formData.append("file", file)

  const response = await apiClient.put<{
    success: boolean
    data: { id: string; filePath: string; isCover: boolean }
    message: string
  }>(`/api/rituals/${ritualId}/images/${uploadId}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  })

  return response.data.data
}

/**
 * Publish a draft ritual
 */
export async function publishRitual(ritualId: string): Promise<void> {
  await apiClient.patch(`/api/rituals/${ritualId}/publish`)
}

/**
 * Set ritual cover image
 */
export async function setRitualCoverImage(ritualId: string, uploadId: string): Promise<void> {
  await apiClient.patch(`/api/rituals/${ritualId}/images/${uploadId}/cover`)
}

// ========================================
// Community Rituals
// ========================================

export interface CommunityRitual {
  id: string
  title: string
  type: RitualType
  participationFee: ParticipationFee
  price?: number
  organizerCount?: number
  chatCount?: number
  duration: number
  date: string
  location?: string
  memberCount?: number
  savedCount?: number
  totalEarnings?: number
  isSaved?: boolean
  isArchived?: boolean
  createdBy: {
    id: string
    username: string | null
    name: string | null
    coverImage: string | null
  }
  uploads?: Array<{
    id: string
    filePath: string
    fileName: string
    isCover: boolean
  }>
  organizerRecordId?: string | null
  inviteNote?: string | null
  slug?: string | null
}

export interface CommunityRitualsResponse {
  rituals: CommunityRitual[]
  total: number
  page: number
  totalPages: number
  hasMore: boolean
}

/**
 * Get community rituals
 * Gets rituals where community is an organizer
 */
export async function getCommunityRituals(
  communityId: string,
  params?: {
    page?: number
    limit?: number
    search?: string
    status?: "all" | "archived" | "requests" | "joined"
  },
): Promise<CommunityRitualsResponse> {
  const queryParams = new URLSearchParams()
  if (params?.page) queryParams.append("page", params.page.toString())
  if (params?.limit) queryParams.append("limit", params.limit.toString())
  if (params?.search) queryParams.append("search", params.search)
  if (params?.status) queryParams.append("status", params.status)

  const queryString = queryParams.toString()
  const url = `/api/rituals/community/${communityId}${queryString ? `?${queryString}` : ""}`

  const response = await apiClient.get<{
    success: boolean
    data: CommunityRitualsResponse
    message: string
  }>(url)

  return response.data.data
}

/**
 * Ritual Statistics Response interface
 */
export interface RitualStatistics {
  all: number
  archived: number
  requests: number
}

/**
 * Get community ritual statistics
 * Gets counts of rituals (all, archived, requests) for a community
 */
export async function getCommunityRitualStatistics(communityId: string): Promise<RitualStatistics> {
  const response = await apiClient.get<{
    success: boolean
    data: RitualStatistics
    message: string
  }>(`/api/communities/${communityId}/rituals/statistics`)

  return response.data.data
}

// ========================================
// Community Ritual Requests
// ========================================

export interface CommunityRitualRequest {
  id: string
  organizerRecordId: string
  title: string
  type: string
  participationFee: ParticipationFee
  price: number | null
  duration: number
  date: string | null
  location: string
  coverImage: string | null
  inviteNote: string | null
  ritualCreatorId: string
  inviter: {
    id: string
    name: string | null
    username: string | null
    avatar: string | null
  } | null
}

export interface CommunityRitualRequestsResponse {
  requests: CommunityRitualRequest[]
  total: number
  page: number
  totalPages: number
  hasMore: boolean
}

export async function getCommunityRitualRequests(
  communityId: string,
  params?: { page?: number; limit?: number; search?: string },
): Promise<CommunityRitualRequestsResponse> {
  const queryParams = new URLSearchParams()
  if (params?.page) queryParams.append("page", params.page.toString())
  if (params?.limit) queryParams.append("limit", params.limit.toString())
  if (params?.search) queryParams.append("search", params.search)

  const queryString = queryParams.toString()
  const url = `/api/communities/${communityId}/ritual-requests${queryString ? `?${queryString}` : ""}`

  const response = await apiClient.get<{
    success: boolean
    data: CommunityRitualRequestsResponse
    message: string
  }>(url)

  return response.data.data
}

// ========================================
// Video Progress Tracking
// ========================================

export interface UpdateVideoProgressRequest {
  moduleId: string
  secondsWatched: number
  status?: "IN_PROGRESS" | "COMPLETED"
  completedAt?: string // ISO date string
}

export interface UpdateVideoProgressResponse {
  id: string
  secondsWatched: number
  status: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED"
  completedAt: string | null
}

/**
 * Update video progress for a course module
 * Creates or updates a video progress record
 */
export async function updateVideoProgress(
  ritualId: string,
  data: UpdateVideoProgressRequest,
): Promise<UpdateVideoProgressResponse> {
  const response = await apiClient.patch<{
    success: boolean
    data: UpdateVideoProgressResponse
    message: string
  }>(`/api/rituals/${ritualId}/progress`, data)

  return response.data.data
}

/**
 * Archive a ritual for a community
 * Sets isArchived to true for the community-ritual relationship
 */
export async function archiveRitualForCommunity(
  ritualId: string,
  communityId: string,
): Promise<void> {
  await apiClient.patch<{
    success: boolean
    data: null
    message: string
  }>(`/api/rituals/${ritualId}/communities/${communityId}/archive`)
}

/**
 * Unarchive a ritual for a community
 * Sets isArchived to false for the community-ritual relationship
 */
export async function unarchiveRitualForCommunity(
  ritualId: string,
  communityId: string,
): Promise<void> {
  await apiClient.patch<{
    success: boolean
    data: null
    message: string
  }>(`/api/rituals/${ritualId}/communities/${communityId}/unarchive`)
}

// ========================================
// Self-Guided Ritual Progress API
// ========================================

export interface StartRitualResponse {
  id: string
  status: string
  startedAt: string
}

export interface CompleteRitualResponse {
  id: string
  status: string
  completedAt: string
}

/**
 * Start a self-guided ritual
 * Creates a progress entry with status = IN_PROGRESS
 */
export async function startSelfGuidedRitual(ritualId: string): Promise<StartRitualResponse> {
  const response = await apiClient.post<{
    success: boolean
    data: StartRitualResponse
    message: string
  }>(`/api/rituals/${ritualId}/start`)

  return response.data.data
}

/**
 * Mark a self-guided ritual as completed
 * Updates the progress entry status to COMPLETED
 */
export async function completeSelfGuidedRitual(ritualId: string): Promise<CompleteRitualResponse> {
  const response = await apiClient.post<{
    success: boolean
    data: CompleteRitualResponse
    message: string
  }>(`/api/rituals/${ritualId}/complete`)

  return response.data.data
}

/**
 * Accept a ritual organizer invite
 */
export async function acceptOrganizerInvite(
  ritualId: string,
  inviteId: string,
): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.post<{
    success: boolean
    data: { success: boolean; message: string }
    message: string
  }>(`/api/rituals/${ritualId}/organizers/${inviteId}/accept`)

  return response.data.data
}

/**
 * Reject a ritual organizer invite
 */
export async function rejectOrganizerInvite(
  ritualId: string,
  inviteId: string,
): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.post<{
    success: boolean
    data: { success: boolean; message: string }
    message: string
  }>(`/api/rituals/${ritualId}/organizers/${inviteId}/reject`)

  return response.data.data
}

/**
 * Resend a ritual organizer invite
 */
export async function resendOrganizerInvite(
  ritualId: string,
  organizerId: string,
  inviteNote?: string,
): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.post<{
    success: boolean
    data: { success: boolean; message: string }
    message: string
  }>(`/api/rituals/${ritualId}/organizers/${organizerId}/resend`, {
    inviteNote,
  })

  return response.data.data
}

/**
 * Withdraw a ritual organizer invite (admin action - hard delete)
 */
export async function withdrawOrganizerInvite(
  ritualId: string,
  inviteId: string,
): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.delete<{
    success: boolean
    data: { success: boolean; message: string }
    message: string
  }>(`/api/rituals/${ritualId}/organizers/${inviteId}/withdraw`)

  return response.data.data
}

export interface RitualMemberInvitee {
  id: string | null
  userId: string
  email: string | null
  status: "INVITED" | "JOINED" | "DECLINED" | "not_invited"
  invitedAt: string | null
  joinedAt: string | null
  user: {
    id: string
    name: string
    username: string | null
    profilePictureUrl: string | null
  } | null
}

/**
 * Get ritual member invitees
 */
export async function getRitualMemberInvitees(ritualId: string): Promise<RitualMemberInvitee[]> {
  const response = await apiClient.get<{
    success: boolean
    data: RitualMemberInvitee[]
    message: string
  }>(`/api/rituals/${ritualId}/members/invitees`)

  return response.data.data
}

/**
 * Get ritual members (joined)
 */
export async function getRitualMembers(
  ritualId: string,
  page: number = 1,
  limit: number = 20,
  search?: string,
): Promise<RitualMembersResponse> {
  const response = await apiClient.get<{
    success: boolean
    data: RitualMembersResponse
    message: string
  }>(`/api/rituals/${ritualId}/members`, {
    params: { page, limit, search: search || undefined },
  })

  return response.data.data
}

/**
 * Get users who have saved a ritual
 */
export async function getRitualSavedUsers(
  ritualId: string,
  page: number = 1,
  limit: number = 20,
  search?: string,
): Promise<RitualSavedUsersResponse> {
  const response = await apiClient.get<{
    success: boolean
    data: RitualSavedUsersResponse
    message: string
  }>(`/api/rituals/${ritualId}/saved-users`, {
    params: { page, limit, search: search || undefined },
  })

  return response.data.data
}

/**
 * Invite a user to join a ritual as a member
 */
export async function inviteRitualMember(
  ritualId: string,
  userId: string,
): Promise<{ success: boolean; message: string; inviteId: string }> {
  const response = await apiClient.post<{
    success: boolean
    data: { success: boolean; message: string; inviteId: string }
    message: string
  }>(`/api/rituals/${ritualId}/invite`, { userId })

  return response.data.data
}

/**
 * Accept a ritual member invite
 */
export async function acceptRitualMemberInvite(
  ritualId: string,
  inviteId: string,
): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.post<{
    success: boolean
    data: { success: boolean; message: string }
    message: string
  }>(`/api/rituals/${ritualId}/invite/${inviteId}/accept`)

  return response.data.data
}

/**
 * Reject a ritual member invite
 */
export async function rejectRitualMemberInvite(
  ritualId: string,
  inviteId: string,
): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.post<{
    success: boolean
    data: { success: boolean; message: string }
    message: string
  }>(`/api/rituals/${ritualId}/invite/${inviteId}/reject`)

  return response.data.data
}

/**
 * Resend a ritual member invite
 */
export async function resendRitualMemberInvite(
  ritualId: string,
  inviteId: string,
): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.post<{
    success: boolean
    data: { success: boolean; message: string }
    message: string
  }>(`/api/rituals/${ritualId}/invite/${inviteId}/resend`)

  return response.data.data
}

/**
 * Withdraw a ritual member invite
 */
export async function withdrawRitualMemberInvite(
  ritualId: string,
  inviteId: string,
): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.delete<{
    success: boolean
    data: { success: boolean; message: string }
    message: string
  }>(`/api/rituals/${ritualId}/invite/${inviteId}/withdraw`)

  return response.data.data
}

// ============================================================================
// Ritual Join Request API Functions (for invite_req privacy setting)
// ============================================================================

export interface RitualJoinRequest {
  id: string
  userId: string
  status: "PENDING" | "ACCEPTED" | "REJECTED" | "CANCELLED"
  requestedAt: string
  user: {
    id: string
    name: string | null
    username: string | null
    profilePictureUrl: string | null
  }
}

export interface RitualJoinRequestsResponse {
  items: RitualJoinRequest[]
  total: number
  totalPages: number
}

/**
 * Request to join a ritual with invite_req setting
 */
export async function requestToJoinRitual(
  ritualId: string,
): Promise<{ success: boolean; message: string; requestId: string }> {
  const response = await apiClient.post<{
    success: boolean
    data: { success: boolean; message: string; requestId: string }
    message: string
  }>(`/api/rituals/${ritualId}/request-join`)

  return response.data.data
}

/**
 * Get ritual join requests (for organizers)
 */
export async function getRitualJoinRequests(
  ritualId: string,
  options?: {
    status?: "PENDING" | "ACCEPTED" | "REJECTED" | "CANCELLED"
    page?: number
    limit?: number
  },
): Promise<RitualJoinRequestsResponse> {
  const { status = "PENDING", page = 1, limit = 20 } = options || {}
  const response = await apiClient.get<{
    success: boolean
    data: RitualJoinRequestsResponse
  }>(`/api/rituals/${ritualId}/requests`, {
    params: { status, page, limit },
  })

  return response.data.data
}

/**
 * Accept a join request
 */
export async function acceptJoinRequest(
  ritualId: string,
  requestId: string,
): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.post<{
    success: boolean
    data: { success: boolean; message: string }
    message: string
  }>(`/api/rituals/${ritualId}/requests/${requestId}/accept`)

  return response.data.data
}

/**
 * Reject a join request
 */
export async function rejectJoinRequest(
  ritualId: string,
  requestId: string,
  reason?: string,
): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.post<{
    success: boolean
    data: { success: boolean; message: string }
    message: string
  }>(`/api/rituals/${ritualId}/requests/${requestId}/reject`, { reason })

  return response.data.data
}

/**
 * Cancel own join request
 */
export async function cancelJoinRequest(
  ritualId: string,
): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.delete<{
    success: boolean
    data: { success: boolean; message: string }
    message: string
  }>(`/api/rituals/${ritualId}/requests/cancel`)

  return response.data.data
}

/**
 * Delete a ritual (admin/owner only)
 * Permanently deletes a ritual created by the user
 */
export async function deleteRitual(
  ritualId: string,
): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.delete<{
    success: boolean
    data: { success: boolean; message: string }
    message: string
  }>(`/api/rituals/${ritualId}`)

  return response.data.data
}

/**
 * Admin-complete a ritual
 * Permanently marks a ritual as completed by the owner.
 * Rejects all pending join requests, declines pending organizer invites.
 */
export async function adminCompleteRitual(ritualId: string): Promise<void> {
  await apiClient.post(`/api/rituals/${ritualId}/admin-complete`)
}

export async function archiveRitual(ritualId: string): Promise<void> {
  await apiClient.patch(`/api/rituals/${ritualId}/archive`)
}

export async function unarchiveRitual(ritualId: string): Promise<void> {
  await apiClient.patch(`/api/rituals/${ritualId}/unarchive`)
}

export interface RitualDuplicateData {
  title: string
  type: RitualType
  date: string | null
  repeat: RepeatOption
  repeatAt?: string | null
  repeatAtType?: "DAYS" | "HOURS" | "DATE" | null
  duration: string
  durationType: string | null
  streetAddress: string | null
  city: string | null
  state: string | null
  lat: number | null
  lng: number | null
  country: string | null
  participationFee: ParticipationFee
  price: number | null
  feeDetails: string | null
  description: string | null
  whatToBring: string | null
  experienceDetails: string | null
  intention: string | null
  intentions: Array<{ id: string; name: string }>
  steps: string | null
  meetingLink: string | null
  webOrSocialMediaLink: string | null
  accessType: RitualVisibility
  whoCanSeeYourRitual: string[]
  whoCanComment: string[]
  whoCanEditRitual: string[]
  whoCanJoin: string[]
  whoCanInvite: string[]
  whoCanSeeParticipantList: string[]
  activateMinimumParticipantRequirement: boolean | null
  minParticipants: number | null
  registrationDeadline: string | null
  tags: Array<{ id: string; text: string }>
  media: Array<{
    url: string
    isCover: boolean
    fileName: string
    fileType: string
    uploadId?: string
  }>
  videos: VideoInput[]
  courseModules: Array<{
    title: string
    description: string
    duration: string
    video_url: string | null
    thumbnail_url: string | null
    uploadId: string | null
  }>
  organizers: RitualDuplicateOrganizer[]
}

export async function getRitualDuplicateData(ritualId: string): Promise<RitualDuplicateData> {
  const response = await apiClient.get<{ success: boolean; data: RitualDuplicateData }>(
    `/api/rituals/${ritualId}/duplicate-data`,
  )
  return response.data.data
}
